import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MobileShell } from "@/components/MobileShell";
import { BrandHeader } from "@/components/BrandHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Lock, Unlock, Plus, Loader2, PiggyBank, ArrowDownLeft, ArrowUpRight, History } from "lucide-react";
import {
  listMyVaults,
  createVault,
  lockToVault,
  unlockFromVault,
  getWalletAvailable,
  listMyVaultHistory,
  type Vault,
  type VaultLedgerEntry,
  type VaultType,
  type WalletAvailable,
} from "@/domains/vault";

const money = (c: number) => `$${(c / 100).toFixed(2)}`;

type AmountAction = { vault: Vault | null; mode: "lock" | "unlock" } | null;

const Vaults = () => {
  const navigate = useNavigate();
  const [vaults, setVaults] = useState<Vault[]>([]);
  const [wallet, setWallet] = useState<WalletAvailable | null>(null);
  const [history, setHistory] = useState<VaultLedgerEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);

  // amount dialog
  const [amountAction, setAmountAction] = useState<AmountAction>(null);
  const [amountVaultId, setAmountVaultId] = useState("");
  const [amountInput, setAmountInput] = useState("");
  const [amountBusy, setAmountBusy] = useState(false);

  // create form
  const [name, setName] = useState("");
  const [type, setType] = useState<VaultType>("manual");
  const [goalDollars, setGoalDollars] = useState("");
  const [autoPercent, setAutoPercent] = useState("10");
  const [unlockDelayHours, setUnlockDelayHours] = useState("0");
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [vs, w, h] = await Promise.all([listMyVaults(), getWalletAvailable(), listMyVaultHistory(25)]);
      setVaults(vs);
      setWallet(w);
      setHistory(h);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Name required");
    setBusy(true);
    try {
      await createVault({
        name: name.trim(),
        type,
        goalCents: Math.round((Number(goalDollars) || 0) * 100),
        autoPercentBps: type === "auto" ? Math.round((Number(autoPercent) || 0) * 100) : 0,
        unlockDelaySeconds: type === "goal" ? Math.round((Number(unlockDelayHours) || 0) * 3600) : 0,
      });
      toast.success("Vault created");
      setCreateOpen(false);
      setName(""); setGoalDollars(""); setAutoPercent("10"); setUnlockDelayHours("0"); setType("manual");
      await refresh();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const openAmount = (vault: Vault, mode: "lock" | "unlock") => {
    setAmountVaultId(vault.id);
    setAmountInput("");
    setAmountAction({ vault, mode });
  };

  const openTotalLocked = () => {
    if (vaults.length === 0) return toast.error("Create a vault first");
    setAmountVaultId(vaults[0].id);
    setAmountInput("");
    setAmountAction({ vault: null, mode: "lock" });
  };

  const submitAmount = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!amountAction) return;
    const cents = Math.round(Number(amountInput) * 100);
    if (!cents || cents <= 0) return toast.error("Invalid amount");
    const { mode } = amountAction;
    const vault = amountAction.vault ?? vaults.find((v) => v.id === amountVaultId) ?? null;
    if (!vault) return toast.error("Choose a vault");
    setAmountBusy(true);
    try {
      if (mode === "lock") {
        await lockToVault(vault.id, cents);
        toast.success(`Locked ${money(cents)}`);
      } else {
        await unlockFromVault(vault.id, cents);
        toast.success(`Unlocked ${money(cents)} (−2 discipline pts)`);
      }
      setAmountAction(null);
      await refresh();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setAmountBusy(false);
    }
  };

  const totalLocked = vaults.reduce((s, v) => s + v.locked_cents, 0);
  const vaultNameById = new Map(vaults.map((v) => [v.id, v.name]));

  // Compute the Total Locked balance immediately AFTER each history event.
  // history is newest-first; the most recent event leaves totalLocked at its current value.
  const historyWithBalance = (() => {
    const rows: Array<{ entry: VaultLedgerEntry; balanceAfter: number }> = [];
    let runningAfter = totalLocked;
    for (const entry of history) {
      rows.push({ entry, balanceAfter: runningAfter });
      // step "back in time": undo this event to get the balance after the previous event
      runningAfter -= entry.direction === "lock" ? entry.amount_cents : -entry.amount_cents;
    }
    return rows;
  })();

  const formatWhen = (iso: string) => {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const mins = Math.round(diffMs / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  };


  return (
    <MobileShell>
      <BrandHeader onBack={() => navigate(-1)} curve>
        <h1 className="text-primary-foreground text-2xl font-display font-bold">Cash Vaults</h1>
      </BrandHeader>

      <div className="-mt-6 mx-4 bg-background rounded-2xl shadow-card p-5 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Card className="p-3">
            <div className="text-xs text-muted-foreground">Spendable</div>
            <div className="text-xl font-bold">{money(wallet?.available_cents ?? 0)}</div>
          </Card>
          <button
            type="button"
            onClick={openTotalLocked}
            className="rounded-lg border bg-card p-3 text-left text-card-foreground shadow-sm transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            aria-label="Lock funds into a cash vault"
          >
            <div className="text-xs text-muted-foreground">Total locked</div>
            <div className="text-xl font-bold">{money(totalLocked)}</div>
          </button>
        </div>

        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button variant="hero" size="lg" className="w-full">
              <Plus className="h-5 w-5 mr-2" /> New vault
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create vault</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-3">
              <div>
                <Label>Name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Travel fund" />
              </div>
              <div>
                <Label>Type</Label>
                <Select value={type} onValueChange={(v) => setType(v as VaultType)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manual — lock when you choose</SelectItem>
                    <SelectItem value="auto">Auto — save % of every incoming payment</SelectItem>
                    <SelectItem value="goal">Goal — savings target with unlock delay</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {type !== "manual" && (
                <div>
                  <Label>Goal (USD, optional)</Label>
                  <Input type="number" min="0" step="0.01" value={goalDollars} onChange={(e) => setGoalDollars(e.target.value)} placeholder="1000" />
                </div>
              )}
              {type === "auto" && (
                <div>
                  <Label>Auto-save % of each incoming payment (0–50)</Label>
                  <Input type="number" min="0" max="50" step="0.5" value={autoPercent} onChange={(e) => setAutoPercent(e.target.value)} />
                </div>
              )}
              {type === "goal" && (
                <div>
                  <Label>Unlock delay (hours)</Label>
                  <Input type="number" min="0" step="1" value={unlockDelayHours} onChange={(e) => setUnlockDelayHours(e.target.value)} />
                </div>
              )}
              <Button type="submit" variant="hero" className="w-full" disabled={busy}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create vault"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {loading ? (
          <div className="grid place-items-center py-10 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : vaults.length === 0 ? (
          <Card className="p-8 text-center text-muted-foreground">
            <PiggyBank className="h-10 w-10 mx-auto mb-2 opacity-60" />
            No vaults yet. Create one to start saving.
          </Card>
        ) : (
          <div className="space-y-3">
            {vaults.map((v) => {
              const pct = v.goal_cents > 0 ? Math.min(100, (v.locked_cents / v.goal_cents) * 100) : 0;
              return (
                <Card key={v.id} className="p-4 space-y-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-semibold">{v.name}</div>
                      <div className="text-xs text-muted-foreground capitalize">
                        {v.type}
                        {v.type === "auto" && ` · ${(v.auto_percent_bps / 100).toFixed(1)}% auto`}
                        {v.type === "goal" && v.unlock_delay_seconds > 0 && ` · ${Math.round(v.unlock_delay_seconds / 3600)}h delay`}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold">{money(v.locked_cents)}</div>
                      {v.goal_cents > 0 && <div className="text-xs text-muted-foreground">of {money(v.goal_cents)}</div>}
                    </div>
                  </div>
                  {v.goal_cents > 0 && <Progress value={pct} />}
                  <div className="flex gap-2 pt-1">
                    <Button size="sm" variant="secondary" className="flex-1" onClick={() => openAmount(v, "lock")}>
                      <Lock className="h-4 w-4 mr-1" /> Lock
                    </Button>
                    <Button size="sm" variant="outline" className="flex-1" onClick={() => openAmount(v, "unlock")} disabled={v.locked_cents === 0}>
                      <Unlock className="h-4 w-4 mr-1" /> Unlock
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        <div className="pt-2">
          <div className="flex items-center gap-2 mb-2">
            <History className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-sm font-semibold">Lock activity</h2>
          </div>
          {loading ? null : historyWithBalance.length === 0 ? (
            <Card className="p-4 text-center text-xs text-muted-foreground">
              No lock or unlock activity yet.
            </Card>
          ) : (
            <Card className="divide-y">
              {historyWithBalance.map(({ entry, balanceAfter }) => {
                const isLock = entry.direction === "lock";
                return (
                  <div key={entry.id} className="flex items-center gap-3 p-3">
                    <div
                      className={
                        "h-8 w-8 rounded-full grid place-items-center shrink-0 " +
                        (isLock ? "bg-secondary text-secondary-foreground" : "bg-muted text-muted-foreground")
                      }
                      aria-hidden
                    >
                      {isLock ? <ArrowDownLeft className="h-4 w-4" /> : <ArrowUpRight className="h-4 w-4" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">
                        {isLock ? "Locked into" : "Unlocked from"} {vaultNameById.get(entry.vault_id) ?? "vault"}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {formatWhen(entry.created_at)} · Total locked {money(balanceAfter)}
                      </div>
                    </div>
                    <div className={"text-sm font-semibold " + (isLock ? "text-foreground" : "text-muted-foreground")}>
                      {isLock ? "+" : "−"}{money(entry.amount_cents)}
                    </div>
                  </div>
                );
              })}
            </Card>
          )}
        </div>
      </div>


      <Dialog open={!!amountAction} onOpenChange={(o) => !o && setAmountAction(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {amountAction?.mode === "lock" ? "Lock into" : "Unlock from"}{" "}
              {amountAction?.vault ? `“${amountAction.vault.name}”` : "cash vault"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submitAmount} className="space-y-3">
            {!amountAction?.vault && (
              <div>
                <Label>Vault</Label>
                <Select value={amountVaultId} onValueChange={setAmountVaultId}>
                  <SelectTrigger><SelectValue placeholder="Choose vault" /></SelectTrigger>
                  <SelectContent>
                    {vaults.map((v) => (
                      <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Amount (USD)</Label>
              <Input
                type="text"
                inputMode="decimal"
                autoFocus
                value={amountInput}
                onChange={(e) => {
                  const v = e.target.value.replace(",", ".").replace(/[^0-9.]/g, "");
                  const parts = v.split(".");
                  const clean = parts.length > 1 ? parts[0] + "." + parts.slice(1).join("").slice(0, 2) : v;
                  setAmountInput(clean);
                }}
                placeholder="0.00"
              />
              {amountAction?.mode === "unlock" && (
                <p className="text-xs text-muted-foreground mt-2">
                  Unlocking moves funds back to your spendable balance and costs 2 discipline points.
                </p>
              )}
            </div>
            <Button type="submit" variant="hero" className="w-full" disabled={amountBusy}>
              {amountBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : amountAction?.mode === "lock" ? "Lock funds" : "Unlock funds"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </MobileShell>
  );
};

export default Vaults;
